import React from 'react';
import PropTypes from 'prop-types';

import { cn } from 'helpers/classnames';
import useMartyContext from 'hooks/useMartyContext';
import MartyLink from 'components/common/MartyLink';
import MarketplaceAwareLink from 'components/common/MarketplaceAwareLink';
import FinalSale from 'components/common/FinalSale';
import { generateProductUrl, isItemInCancelState, isItemInReturnState } from 'helpers/MyAccountUtils';
import { IS_GIFT_CARD } from 'common/regex';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { track } from 'apis/amethyst';
import { evWriteProductReviewClick } from 'events/productReview';
import { ORDER_DETAIL_PAGE } from 'constants/amethystPageTypes';
import { WRITE_A_REVIEW_CTA } from 'constants/reviews';

import css from 'styles/components/account/orderProduct.scss';

export const OrderProduct = ({ product, price, isLegacy, finalSale, lineItemState, merchantId, hideWriteReview, itemUniqueId }) => {
  const { attributes, retinaImageUrl, name, brand, style, reviewUrl, productId, styleId, stockId, asin } = product;
  const returnState = isItemInReturnState(lineItemState);
  const cancelState = isItemInCancelState(lineItemState);
  const isInSpecialState = returnState || cancelState;
  const { testId, marketplace } = useMartyContext();
  const {
    features: { showReviews },
    checkout: { asinVerbiage = 'ASIN' }
  } = marketplace;
  const isGiftCard = IS_GIFT_CARD.test(brand);

  return (
    <div className={css.wrapper} data-test-id={testId('orderProduct')}>
      <MarketplaceAwareLink
        // Since we have a duplicate link wrapping both this purely decorative image and
        // The Brand/Product itself, we can safely hide this from screen readers/keyboard nav
        aria-hidden={true}
        merchantId={merchantId}
        tabIndex={-1}
        to={generateProductUrl(product, isLegacy)}
        className={css.mProduct}
        data-track-action="My-Favorites"
        data-track-label="Favorites-Product"
        data-track-value="Image-Clicked"
        data-test-id={testId('productImageLink')}
      >
        <div className={cn(css.image, isInSpecialState && css.specialState, returnState && css.returnedState, cancelState && css.cancelledState)}>
          {isInSpecialState && <span>{lineItemState}</span>}
          <img src={retinaImageUrl} alt={name} />
        </div>
      </MarketplaceAwareLink>

      <div id={`aria-orderProductDetails-${itemUniqueId}`} className={css.itemContainer}>
        <MarketplaceAwareLink
          className={css.productTitle}
          merchantId={merchantId}
          to={generateProductUrl(product, isLegacy)}
          data-test-id={testId('productNameLink')}
        >
          <p data-test-id={testId('productBrand')}>{brand}</p>
          <p className={css.productName} data-test-id={testId('productStyle')}>
            {style}
          </p>
        </MarketplaceAwareLink>
        <div className={css.itemPrice} data-test-id={testId('productPrice')}>
          {price}
        </div>
        <div className={css.itemDetails}>
          {attributes.map(
            ({ key, value }) =>
              !(key === 'Color' && isGiftCard) && (
                <dl key={key}>
                  <dt>{key === 'ASIN' ? asinVerbiage : key}:</dt>
                  <dd data-test-id={testId(`attribute-${key}`)}>{value}</dd>
                </dl>
              )
          )}
          {finalSale && <FinalSale />}
          {showReviews && reviewUrl && !hideWriteReview && (
            <p className={css.reviewUrl}>
              <MartyLink
                to={reviewUrl}
                onClick={() => {
                  track(() => [
                    evWriteProductReviewClick,
                    {
                      productId,
                      styleId,
                      stockId,
                      asin,
                      addedFrom: ORDER_DETAIL_PAGE
                    }
                  ]);
                }}
                data-test-id={testId('productReviewLink')}
              >
                {WRITE_A_REVIEW_CTA}
              </MartyLink>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

OrderProduct.propTypes = {
  product: PropTypes.object.isRequired,
  price: PropTypes.string
};

OrderProduct.displayName = 'OrderProduct';

export default withErrorBoundary(OrderProduct.displayName, OrderProduct);
